'use client';

import { useSuspenseQuery } from '@tanstack/react-query';
import { useRef, useState, type JSX, type ChangeEventHandler } from 'react';

import { Text } from '~/components/text';
import { Show } from '~/components/show';
import { cn, formatMoney } from '~/utils';
import { Input } from '~/components/input';
import { maximumNumber } from '~/constants';
import { GstTag } from '~/components/gst-tag';
import { Separator } from '~/components/separator';
import { CaretDownIcon } from '~/components/icons';
import { RadioGroup } from '~/components/radio-group';
import { platformSettingsOptions } from '~/features/settings/querties';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/popover';
import {
  Command,
  CommandList,
  CommandItem,
  CommandGroup,
} from '~/components/command';

interface IPricingProps {
  unit: string;
  title: string;
  [key: string]: string;
}

interface IUnitOptionProps {
  label: string;
  value: string;
}

interface IInputPricingProps {
  priceName?: string;
  hasTitle?: boolean;
  placeholder?: string;
  keepFields?: string[];
  value?: IPricingProps;
  unitPlaceholder?: string;
  isShowGstValue?: boolean;
  titlePlaceholder?: string;
  quantityPlaceholder?: string;
  defaultValue?: IPricingProps;
  unitOptions?: IUnitOptionProps[];
  onChange?: (value: IPricingProps) => void;
}

const getKeepFields = (
  keepFields: string[],
  value: IPricingProps,
  defaultValue?: IPricingProps,
) => {
  if (!keepFields || keepFields.length === 0) {
    return {};
  }

  return keepFields.reduce(
    (result, field) => {
      if (!value?.[field]) {
        return result;
      }
      result[field] = value?.[field] || defaultValue?.[field] || '';

      return result;
    },
    {} as Record<string, string>,
  );
};

export function InputPricing({
  value,
  onChange,
  defaultValue,
  hasTitle = false,
  unitOptions = [],
  priceName = 'price',
  placeholder = '200',
  isShowGstValue = false,
  titlePlaceholder = 'Title',
  quantityPlaceholder = 'Quantity',
  unitPlaceholder = 'Select price unit',
  keepFields = [],
  ...rest
}: IInputPricingProps): JSX.Element {
  const titleRef = useRef<HTMLInputElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);
  const groupedUnitOptions = unitOptions.reduce<
    Record<string, IUnitOptionProps>
  >((result, option) => {
    result[option.value] = option;

    return result;
  }, {});
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const {
    data: { currency },
  } = useSuspenseQuery(platformSettingsOptions());

  const [price, setPrice] = useState(
    value?.[priceName] || defaultValue?.[priceName] || '',
  );
  const [displayedNumber, setDisplayedNumber] = useState(
    formatMoney({ value: value?.[priceName] || defaultValue?.[priceName] || '', currency }),
  );
  const [unit, setUnit] = useState<IUnitOptionProps | undefined>(
    groupedUnitOptions[value?.unit || defaultValue?.unit || ''],
  );

  const isKeepFields = keepFields && keepFields.length > 0;

  const handleChangePrice: ChangeEventHandler<HTMLInputElement> = (event) => {
    let inputValue = event.target.value
      .replace(/[^\.\d]/g, '')
      .replace(/^(\d+\.\d{2}).*$/, '$1');

    if (Number(inputValue) > maximumNumber) {
      inputValue = maximumNumber.toString();
    }

    onChange?.({
      [priceName]: inputValue,
      unit: unit?.value || '',
      title: titleRef.current?.value || '',
      quantity: quantityRef.current?.value || '',
      ...(isKeepFields &&
        value &&
        getKeepFields(keepFields, value, defaultValue)),
    });

    setPrice(inputValue);
    setDisplayedNumber(inputValue);
  };

  const handleSelectUnit: IPriceUnitProps['onSelect'] = (unit) => {
    setUnit(unit);

    onChange?.({
      unit: unit.value,
      [priceName]: price,
      title: titleRef.current?.value || '',
      ...(isKeepFields &&
        value &&
        getKeepFields(keepFields, value, defaultValue)),
    });
  };

  const handleChangeTitle: ChangeEventHandler<HTMLInputElement> = (event) => {
    onChange?.({
      [priceName]: price,
      unit: unit?.value || '',
      title: event.target.value,
      ...(isKeepFields &&
        value &&
        getKeepFields(keepFields, value, defaultValue)),
    });
  };

  const handleChangeQuantity: ChangeEventHandler<HTMLInputElement> = (event) => {
    onChange?.({
      unit: unit?.value || '',
      [priceName]: price,
      title: titleRef.current?.value || '',
      quantity: event.target.value,
      ...(isKeepFields &&
        value &&
        getKeepFields(keepFields, value, defaultValue)),
    });
  };

  const handleFocus = () => {
    setFocused(true);
    setDisplayedNumber(price);
  };

  const handleBlur = () => {
    setFocused(false);
    setDisplayedNumber(formatMoney({ value: price, currency }));
  };

  return (
    <div
      {...rest}
      className={cn(
        'flex border border-[#ececec] rounded-md',
        'hover:border-[#cdcdcd] aria-invalid:border-desire-500',
        (open || focused) &&
          'border-blueBonnet-500 hover:border-blueBonnet-500',
        hasTitle && 'flex-col h-auto items-start md:flex md:items-center',
      )}
    >
      <div className='flex gap-3 py-[12.5px] md:h-[46px] max-md:flex-col w-full max-md:items-start'>
        <Show when={hasTitle}>
          <Input
            ref={titleRef}
            onChange={handleChangeTitle}
            placeholder={titlePlaceholder}
            defaultValue={value?.title || defaultValue?.title}
            className={cn(
              'border-0 p-0 pl-4',
              'placeholder:text-[#bfbfbf] shadow-none h-[unset]',
            )}
          />
          <Separator orientation='vertical' className='max-md:hidden' />
        </Show>
        <Input
          min={0}
          onBlur={handleBlur}
          onFocus={handleFocus}
          value={displayedNumber}
          placeholder={formatMoney({ value: placeholder, currency })}
          onChange={handleChangePrice}
          className={cn(
            'border-0 p-0 pl-4',
            'placeholder:text-[#bfbfbf] shadow-none h-[unset]',
          )}
        />
        <Show when={unit?.value === 'per_hour'}>
          <Separator orientation='vertical' className='max-md:hidden' />
          <Input
            ref={quantityRef}
            placeholder={quantityPlaceholder}
            value={value?.quantity || defaultValue?.quantity}
            onChange={handleChangeQuantity}
            className={cn(
              'border-0 p-0 pl-4',
              'placeholder:text-[#bfbfbf] shadow-none h-[unset]',
            )}
          />
        </Show>
        <Separator orientation='vertical' className='max-md:hidden' />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            tabIndex={-1}
            className={cn(
              'flex gap-2 items-center min-w-[116px] pr-4 cursor-pointer shrink-0',
              hasTitle && 'pl-4 md:pl-0',
            )}
          >
            <div className='flex gap-2 items-center flex-1'>
              <Show when={!unit}>
                <Text className='text-[#bfbfbf]'>{unitPlaceholder}</Text>
              </Show>
              <Show when={unit}>
                <Text className='text-[16px] leading-[130%]'>
                  {unit?.label}
                </Text>
              </Show>
            </div>
            <CaretDownIcon className='size-4' />
          </PopoverTrigger>
          <PopoverContent
            align='end'
            side='bottom'
            sideOffset={19.5}
            className='p-0 w-full'
            style={{
              width: containerRef.current
                ? `${containerRef.current.offsetWidth}px`
                : '100%',
            }}
          >
            <Command className='w-full'>
              <CommandList>
                <CommandGroup className='p-0'>
                  <RadioGroup className='gap-0' value={unit?.value}>
                    {unitOptions.map((u) => {
                      return (
                        <PriceUnit
                          unit={u}
                          key={u.value}
                          onSelect={handleSelectUnit}
                          selected={u.value === unit?.value}
                        />
                      );
                    })}
                  </RadioGroup>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <Show when={isShowGstValue}>
        <Separator />
        <GstTag
          as='div'
          className='w-full py-[12.5px] h-[46px] pl-4'
          value={Number(price)}
        />
      </Show>
    </div>
  );
}

interface IPriceUnitProps {
  selected?: boolean;
  unit: IUnitOptionProps;
  onSelect?: (unit: IUnitOptionProps) => void;
}

function PriceUnit({ unit, selected, onSelect }: IPriceUnitProps): JSX.Element {
  const { value, label } = unit;

  const handleSelectUnit = () => {
    onSelect?.(unit);
  };

  return (
    <CommandItem
      key={value}
      value={label}
      onSelect={handleSelectUnit}
      className={cn(
        'items-center gap-2 px-4 py-[12.5px] data-[selected=true]:bg-[#f9f9f9]',
        selected && 'bg-[#f9f9f9]',
      )}
    >
      <div className='flex flex-1 gap-2 items-center w-full'>
        <Text className='text-[16px] leading-[130%]'>{label}</Text>
      </div>
    </CommandItem>
  );
}
